<script setup lang="ts">
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';

const props = defineProps<{
  name: string;
  position: string;
  company?: string;
  photoUrl: string | null;
}>();

const photoSrc = computed(() =>
  resizeImage(props.photoUrl, { width: 200, height: 200, mode: 'fill' }),
);

const photoSrcset = computed(() =>
  resizeImageSrcsetWidths(
    props.photoUrl,
    [60, 80, 120, 160, 200, 240, 400, 600],
    {
      mode: 'fill',
      aspectRatio: 1,
    },
  ),
);
</script>

<template>
  <div :class="$style.hero">
    <div :class="$style.avatar">
      <img
        v-if="photoUrl"
        :src="photoSrc"
        :srcset="photoSrcset || undefined"
        sizes="(max-width: 360px) 60px, (max-width: 1023px) 80px, 200px"
        :alt="name"
        :class="$style.photo"
      />
      <div v-else :class="$style.placeholder" />
    </div>
    <div :class="$style.info">
      <h1 :class="$style.name">{{ name }}</h1>
      <div :class="$style.position">
        {{ position }}{{ company ? `, ${company}` : '' }}
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.hero {
  display: flex;
  align-items: center;
  gap: var(--space-5x);
}

.avatar {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--color-black-1);
}

.photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.placeholder {
  width: 100%;
  height: 100%;
  background-color: var(--color-gray-bg);
}

.info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2x);
  min-width: 0;
}

.name {
  @include text_h1;
  color: var(--color-black-1);
  margin: 0;
}

.position {
  @include text_article_body1_medium;
  color: var(--color-black-1);
}

@media (max-width: 1379px) {
  .hero {
    gap: var(--space-2-5x);
  }

  .name {
    @include text_h4;
  }
}

@media (max-width: 1023px) {
  .avatar {
    width: 80px;
    height: 80px;
  }
}

@media (max-width: 599px) {
  .info {
    gap: 10px;
  }

  .position {
    @include text_body2_medium;
  }
}

@media (max-width: 360px) {
  .avatar {
    width: 60px;
    height: 60px;
  }

  .name {
    @include text_h5;
  }
}
</style>
